import { UploadOutlined } from '@ant-design/icons';
import { Button, Form, Upload } from 'antd';
import { POSTER_LANGUAGE_OPTIONS, THUMBNAIL_UPLOAD_STATE } from 'constants/index';
import { libraryService } from 'content/library/service';
import { libraryStore } from 'content/library/stores/libraryStore';
import { LibraryInterface } from 'content/library/types/contentType';
import { contentService } from 'content/service/contentService';
import { ThumbnailInterface } from 'content/type';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import thumbnailHandlers from '../../handlers/ThumbnailHandlers';
import './Thumbnail.css';
import ThumbnailListComponent from './ThumbnailList';

// Function to normalize language value
const normalizeLanguage = (lang: string | undefined): string | undefined => {
  if (!lang) return undefined;

  const normalized = lang.toUpperCase();

  const mapping: Record<string, string> = {
    KR: 'KO',
    JP: 'JA',
    ZH: 'ZH-HANS',
  };

  if (POSTER_LANGUAGE_OPTIONS.some(opt => opt.value === normalized)) {
    return normalized;
  }

  if (mapping[normalized]) {
    return mapping[normalized];
  }

  return 'KO';
};

const Thumbnail: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const [newThumbnailList, setNewThumbnailList] = useState<ThumbnailInterface[]>([]);
  const [uploadedThumbnailList, setUploadedThumbnailList] = useState<ThumbnailInterface[]>([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState<ThumbnailInterface | null>(null);
  const [removedUids, setRemovedUids] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const {
    handleThumbnailUpload,
    handleChange,
    handleNewThumbnailRemove,
    handleUploadedThumbnailRemove,
    handleThumbnailSelect,
    handleThumbnailChange,
    handleNewThumbnailChange,
  } = thumbnailHandlers({
    form,
    contentId: Number(id),
    selectedThumbnail,
    setNewThumbnailList,
    setUploadedThumbnailList,
    setSelectedThumbnail,
    removedUids,
    setRemovedUids,
  });

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const library: LibraryInterface = await libraryService.fetchLibrary(Number(id));
      if (library.thumbnails) {
        contentService.fetchThumbnailList(Number(id)).then(thumbnailList => {
          library.thumbnails = thumbnailList;
          libraryStore.setLibrary(Number(id), library);
          form.setFieldsValue({
            ...library,
            thumbnails: thumbnailList,
          });
          const uploadedThumbnailList: ThumbnailInterface[] = thumbnailList.map(
            (thumbnail: ThumbnailInterface) => {
              return {
                ...thumbnail,
                state: THUMBNAIL_UPLOAD_STATE.UPLOADED,
                originFileObj: undefined,
                language: normalizeLanguage(thumbnail.language),
              };
            }
          );
          const selectedThumbnail = uploadedThumbnailList.find(
            (thumbnail: ThumbnailInterface) => thumbnail.is_representative
          );
          form.setFieldValue('newThumbnailList', []);
          form.setFieldValue('uploadedThumbnailList', uploadedThumbnailList);
          form.setFieldValue('thumbnailList', uploadedThumbnailList);
          setNewThumbnailList([]);
          setUploadedThumbnailList(uploadedThumbnailList);
          setSelectedThumbnail(selectedThumbnail || null);
        });
      }
    };
    fetchData().then(() => {
      setIsLoading(false);
    });
  }, [form]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="thumbnail-container">
      <div style={{ display: 'flex' }}>
        <div className="thumbnail-preview-container">
          <span> Selected Poster </span>
          <img
            src={selectedThumbnail ? selectedThumbnail.file : ''}
            alt=""
            className="thumbnail-preview"
          />
        </div>
        <div style={{ width: '100%' }}>
          <div className="thumbnail-uploaded-container">
            <Upload
              key={'upload-thumbnail'}
              accept="image/*,.psd"
              multiple={true}
              showUploadList={false}
              beforeUpload={() => {
                return false; // Prevent auto upload
              }}
              onChange={handleChange}
              listType="text"
            >
              <Button icon={<UploadOutlined />} style={{ width: '100%', marginBottom: '10px' }}>
                {`Poster Upload`}
              </Button>
            </Upload>
            <ThumbnailListComponent
              thumbnails={newThumbnailList}
              selectedThumbnail={selectedThumbnail}
              onThumbnailSelect={handleThumbnailSelect}
              onThumbnailRemove={handleNewThumbnailRemove}
              onThumbnailChange={handleThumbnailChange}
              onNewThumbnailChange={handleNewThumbnailChange}
              isNew={true}
            />
          </div>
          <div className="thumbnail-uploaded-container">
            <ThumbnailListComponent
              thumbnails={uploadedThumbnailList}
              selectedThumbnail={selectedThumbnail}
              onThumbnailSelect={handleThumbnailSelect}
              onThumbnailRemove={handleUploadedThumbnailRemove}
              onThumbnailChange={handleThumbnailChange}
              onNewThumbnailChange={handleNewThumbnailChange}
              canDownload={true}
            />
          </div>
        </div>
      </div>
      <Form.Item style={{ marginTop: 16, textAlign: 'center' }}>
        <Button type="primary" onClick={() => handleThumbnailUpload()}>
          Save
        </Button>
      </Form.Item>
    </div>
  );
};

export default Thumbnail;
